import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, User, MessageSquare, Phone, Building2, Headset } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
          >
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-4">
              Reach Out
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#2C2C2A] font-medium mb-6">
              Contact Us
            </h2>
            <p className="font-body text-base leading-relaxed text-[#3A3A38] mb-8 max-w-md">
              Whether you're a fur parent in need, a veterinarian wanting to partner, or someone
              who wants to help &mdash; we'd love to hear from you. Together we can make a difference.
            </p>
            <div className="bg-[#F2EFEB] border border-[#E8E3DD] rounded-3xl p-8">
              <h4 className="font-heading text-xl text-[#2C2C2A] mb-3">333Oreo Foundation</h4>
              <p className="font-body text-sm text-[#6C6B66] mb-1">
                By LaRonda Shree Enterprises LLC
              </p>
              <p className="font-body text-sm text-[#6C6B66] mb-5">
                Animal Lives Matter Too
              </p>

              <div className="space-y-4">
                <a href="tel:8885015441" className="flex items-start gap-3 group" data-testid="contact-hotline">
                  <div className="w-9 h-9 rounded-xl bg-[#C0603A]/10 flex items-center justify-center flex-shrink-0">
                    <Headset className="w-4 h-4 text-[#C0603A]" />
                  </div>
                  <div>
                    <p className="font-body text-xs font-bold tracking-wider uppercase text-[#6C6B66]">Hotline</p>
                    <p className="font-body text-sm font-semibold text-[#2C2C2A] group-hover:text-[#C0603A] transition-colors">(888) 501-5441</p>
                  </div>
                </a>

                <a href="tel:2146440425,101" className="flex items-start gap-3 group" data-testid="contact-extension">
                  <div className="w-9 h-9 rounded-xl bg-[#C0603A]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-[#C0603A]" />
                  </div>
                  <div>
                    <p className="font-body text-xs font-bold tracking-wider uppercase text-[#6C6B66]">LaRonda's Extension</p>
                    <p className="font-body text-sm font-semibold text-[#2C2C2A] group-hover:text-[#C0603A] transition-colors">(214) 644-0425 ext. 101</p>
                  </div>
                </a>

                <a href="tel:7374159398" className="flex items-start gap-3 group" data-testid="contact-corporate">
                  <div className="w-9 h-9 rounded-xl bg-[#C0603A]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-[#C0603A]" />
                  </div>
                  <div>
                    <p className="font-body text-xs font-bold tracking-wider uppercase text-[#6C6B66]">Corporate</p>
                    <p className="font-body text-sm font-semibold text-[#2C2C2A] group-hover:text-[#C0603A] transition-colors">(737) 415-9398</p>
                  </div>
                </a>

                <a href="tel:6784370829" className="flex items-start gap-3 group" data-testid="contact-direct">
                  <div className="w-9 h-9 rounded-xl bg-[#C0603A]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-[#C0603A]" />
                  </div>
                  <div>
                    <p className="font-body text-xs font-bold tracking-wider uppercase text-[#6C6B66]">Direct Line</p>
                    <p className="font-body text-sm font-semibold text-[#2C2C2A] group-hover:text-[#C0603A] transition-colors">(678) 437-0829</p>
                  </div>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.15 }}
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="contact-email-input"
                />
              </div>
              <input
                type="text"
                placeholder="Subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full bg-white border border-[#E8E3DD] rounded-xl px-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                data-testid="contact-subject-input"
              />
              <div className="relative">
                <MessageSquare className="absolute left-4 top-4 w-4 h-4 text-[#6C6B66]" />
                <textarea
                  placeholder="Your Message *"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="bg-[#C0603A] text-white hover:bg-[#A64D29] disabled:opacity-60 transition-all duration-300 rounded-full px-8 py-4 font-body font-semibold text-base inline-flex items-center gap-2 hover:shadow-[0_12px_48px_rgba(192,96,58,0.2)]"
              >
                <Send className="w-4 h-4" />
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
